import { Checkbox, FormControlLabel, Typography, useEventCallback } from '@mui/material';
import { FormikProps } from 'formik';
import { PmiButton } from 'shell/components/shared/button';
import { memo, useEffect, useMemo, useState } from 'react';
import AccordionApplicantGenerality from './accordions/applicant-generality';
import AccordionAccountholderGenerality from './accordions/accountholder-generality';
import Citizenship from './accordions/citizenship';
import ModalInitiative from './accordions/modal-initiative';
import NewRegistrationPersonModal from '../../../../../shell/components/shared/registration-person-sipo-modal';
import { createSearchParams, generatePath, useNavigate } from 'react-router-dom';
import IdentificationDocumentComponent from './accordions/identification-document-applicant';
import ResidenceOriginComponent from '../shared/residence-origin/index';
import IdentificationDocumentAccountholder from './accordions/identification-document-accountholder';
import { __PRACTICE_API_URL__ } from 'shell/constants/environment';
import { useHttpClient } from 'shell/hooks/use-http-fetch/stateless';
import { ResponsePromise } from 'shell/hooks/use-http-fetch/stateless/type';
import { toast } from 'react-toastify';
import { ModalViewerPDF } from 'shell/components/shared/pdf-modal';
import PrintSuccessModalDeclarationDeclarant from './print-success-modal-declaration-declarant';
import { blobToBase64 } from 'shell/utils/base64-to-blob';
import { TypePractice } from 'practice/constants/type-practice';
import { TypeRegister } from 'practice/constants/type-register';
import { DateTime } from 'luxon';
import AccordionResidenceTemporary from './accordions/accordion-residence-temporary';
import { useFilterTable } from 'shell/hooks/use-filter-table';
import {
  transformCitizenship,
  transformGenerality,
} from 'practice/utils/transform-request-step-register-office';
import { ResidenceOrigin } from 'shell/view-models/personal-data';
import { RegisterOfficeVM } from 'shell/view-models/register-office-practice-vm';
import { TypeInitiative } from 'practice/constants/practice-management-step';

interface AccordionStepRegisterOfficeProps {
  formContext: FormikProps<RegisterOfficeVM>;
  initialValues?: RegisterOfficeVM;
  codeEnte?: string;
  yearId?: string;
  typeRegister?: string;
  progressive?: string;
  typePractice?: string;
  idFamily?: string;
  disabled?: boolean;
  disabledAnpr?: boolean;
  disabledElix?: boolean;
  iniziativa?: TypeInitiative;
  setTipoIniziativa: (value?: TypeInitiative) => void;
  onNext: () => void;
  setIntestatarioIsAnpr: (value: boolean) => void;
  ForPracticeTemporary?: boolean;
}

const AccordionStepRegisterOfficeComponent = ({
  formContext,
  initialValues,
  codeEnte,
  yearId,
  typeRegister,
  progressive,
  typePractice,
  disabled,
  disabledAnpr,
  disabledElix,
  iniziativa,
  setTipoIniziativa,
  // onNext,
  setIntestatarioIsAnpr,
  ForPracticeTemporary,
}: AccordionStepRegisterOfficeProps): React.ReactElement => {
  const navigate = useNavigate();

  const [openModalInitiative, setOpenModalInitiative] = useState<boolean>(false);

  const [openModalNewPerson, setOpenModalNewPerson] = useState({ open: false, id: '', idAnpr: '' });
  const [pdfModal, setPdfModal] = useState({ data: '', open: false, name: '' });

  const [isDeclarationSuccessModal, setDeclarationSuccessModal] = useState<boolean>(false);
  const [isConfermaButtonClicked, setConfermaButton] = useState<boolean>(false);

  const { setValues } = useFilterTable();

  const declarationDeclarant = useHttpClient<Blob>('', {
    options: { method: 'POST' },
  });

  const printDeclarationDeclarant = useEventCallback(() => {
    let luogoRilascioDocumento = undefined;

    if (
      formContext?.values?.applicantData?.identificationDocument?.municipality?.description &&
      !formContext.values.applicantData?.identificationDocument?.isForeign
    ) {
      luogoRilascioDocumento =
        formContext?.values?.applicantData?.identificationDocument?.municipality?.description;
    } else if (
      formContext?.values?.applicantData?.identificationDocument?.placeForeign &&
      formContext.values.applicantData?.identificationDocument?.isForeign
    ) {
      luogoRilascioDocumento =
        formContext?.values?.applicantData?.identificationDocument?.placeForeign;
    }

    if (
      formContext?.values?.accountholderData?.identificationDocument?.municipality?.description &&
      !formContext.values.accountholderData?.identificationDocument?.isForeign
    ) {
      luogoRilascioDocumento =
        formContext?.values?.accountholderData?.identificationDocument?.municipality?.description;
    } else if (
      formContext?.values?.accountholderData?.identificationDocument?.placeForeign &&
      formContext.values.accountholderData?.identificationDocument?.isForeign
    ) {
      luogoRilascioDocumento =
        formContext?.values?.accountholderData?.identificationDocument?.placeForeign;
    }

    const data = {
      richiestaDichiarazioneDichiaranteVO: {
        codiceEnte: codeEnte || undefined,
        idAnnoPratica: yearId ? Number.parseInt(yearId, 10) : undefined,
        progressivoPratica: progressive ? Number.parseInt(progressive, 10) : undefined,
        tipoRegistro: typeRegister || undefined,
        dataRilascioDocumento: formContext?.values?.applicantData?.identificationDocument
          ?.issuedDate
          ? formContext?.values?.applicantData?.identificationDocument?.issuedDate.toFormat(
              'yyyy-MM-dd'
            )
          : formContext?.values?.accountholderData?.identificationDocument?.issuedDate
          ? formContext?.values?.accountholderData?.identificationDocument?.issuedDate.toFormat(
              'yyyy-MM-dd'
            )
          : undefined,
        luogoRilascioDocumento,
        numeroDocumento:
          formContext?.values?.applicantData?.identificationDocument?.documentNumber ||
          formContext?.values?.accountholderData?.identificationDocument?.documentNumber ||
          undefined,
      },
      tipoDatiRispostaEstrazioneIntestatarioVO: {
        generalita: transformGenerality(formContext.values.accountholderData?.generality),
        cittadinanza: transformCitizenship(formContext.values.accountholderData?.citizenship),
        residenza: formContext.values.accountholderData?.subjectData?.residenza,
      },
      tipoDatiRispostaEstrazioneRichiedenteVO: formContext?.values?.applicantData?.subjectData
        ? formContext?.values?.applicantData?.subjectData
        : formContext?.values?.applicantData?.generality
        ? {
            generalita: transformGenerality(formContext.values.applicantData?.generality),
          }
        : undefined,
    };

    return (
      declarationDeclarant({
        data: data,
        responseType: 'blob',
        url: ForPracticeTemporary
          ? `${__PRACTICE_API_URL__}/pratiche/stampa/dichiarazione-anagrafe-temporanea`
          : `${__PRACTICE_API_URL__}/pratiche/stampa/dichiarazione-dichiarante`,
      }) as ResponsePromise<Blob>
    ).then((response) => {
      const pdfCertificate = response?.data;
      if (pdfCertificate) {
        const blob = new Blob([pdfCertificate], { type: 'application/pdf' });
        blobToBase64(blob).then((base64) => {
          setPdfModal({
            data: base64.split(',')[1],
            name: 'Riepilogo stampa',
            open: true,
          });
        });
      }
    });
  });

  const updateData = useHttpClient(``, {
    options: { method: 'POST' },
  });

  const onYesNewPerson = useEventCallback(() => {
    setValues({ ...(formContext.values || {}), currentStep: 0 });

    navigate(
      {
        pathname: generatePath(
          '/pratiche/ricerca/modifica/:codiceEnte/:tipoRegistro/:idAnnoPratica/:progressivoPratica/:tipoPratica/nuova-persona',
          {
            codiceEnte: codeEnte || '',
            tipoRegistro: typeRegister || '',
            idAnnoPratica: yearId?.toString() || '',
            progressivoPratica: progressive?.toString() || '',
            tipoPratica: typePractice || '',
          }
        ),
        search:
          openModalNewPerson.id || openModalNewPerson.idAnpr
            ? `?${createSearchParams({
                idIntestatario: openModalNewPerson.id || '0',
                idAnpr: openModalNewPerson.idAnpr || '0',
              })}`
            : '',
      },
      {
        state: {
          accountHolderFiscalCode: formContext.values.accountholderData?.fiscalCode || undefined,
        },
      }
    );
  });

  const isUnavailable = useMemo(() => {
    return typePractice === TypePractice.Unavailable || typeRegister === TypeRegister.Unavailable;
  }, [typePractice, typeRegister]);

  const isUntraceableCensus = useMemo(() => {
    return (
      typePractice === TypePractice.UntraceableCensus ||
      typeRegister === TypeRegister.UntraceableCensus
    );
  }, [typePractice, typeRegister]);

  useEffect(() => {
    if (initialValues) {
      formContext.setValues({
        typePractice,
        applicantIsAccountholder:
          isUnavailable || isUntraceableCensus
            ? false
            : initialValues?.typeInitiative === TypeInitiative.idUfficio
            ? true
            : initialValues?.applicantIsAccountholder || false,
        ...(initialValues || {}),
        accountholderData: {
          ...(initialValues?.accountholderData || {}),
          generality: {
            ...(initialValues?.accountholderData?.generality || {}),
            gender: initialValues?.accountholderData?.generality?.gender || undefined,
            name: initialValues?.accountholderData?.generality?.name || undefined,
            surname: initialValues?.accountholderData?.generality?.surname || undefined,
          },
          identificationDocument: {
            documentNumber:
              initialValues?.accountholderData?.identificationDocument?.documentNumber || undefined,
            issuedDate:
              initialValues?.accountholderData?.identificationDocument?.issuedDate || undefined,
            isForeign: initialValues?.accountholderData?.identificationDocument?.isForeign || false,
            municipality:
              initialValues?.accountholderData?.identificationDocument?.municipality || null,
            placeForeign:
              initialValues?.accountholderData?.identificationDocument?.placeForeign || undefined,
          },
        },
        applicantData: {
          ...(initialValues?.applicantData || {}),
          generality: {
            ...(initialValues?.applicantData?.generality || {}),
            gender: initialValues?.applicantData?.generality?.gender || undefined,
            name: initialValues?.applicantData?.generality?.name || undefined,
            surname: initialValues?.applicantData?.generality?.surname || undefined,
          },
          identificationDocument: {
            documentNumber:
              initialValues?.applicantData?.identificationDocument?.documentNumber || undefined,
            issuedDate:
              initialValues?.applicantData?.identificationDocument?.issuedDate || undefined,
            isForeign: initialValues?.applicantData?.identificationDocument?.isForeign || false,
            municipality:
              initialValues?.applicantData?.identificationDocument?.municipality || null,
            placeForeign:
              initialValues?.applicantData?.identificationDocument?.placeForeign || undefined,
          },
        },
      });
    }
  }, [initialValues]);

  useEffect(() => {
    // se non c'è iniziativa
    if (!Boolean(iniziativa)) {
      // se non è inserito tipo iniziativa chiamare modale per inserimento
      setOpenModalInitiative(true);
    } else {
      setOpenModalInitiative(false);
    }

    if (isUnavailable || isUntraceableCensus) {
      // nel caso di irreperibilità o irreperibilità censimento
      // deve essere false il flag richiedente coincide con intestatario
      formContext.setFieldValue('applicantIsAccountholder', false);

      return;
    }

    if (
      iniziativa === TypeInitiative.idUfficio &&
      !formContext.values.applicantIsAccountholder &&
      typeRegister !== TypeRegister.Residence &&
      typeRegister !== TypeRegister.Emigration
    ) {
      formContext.setFieldValue('applicantIsAccountholder', true);
    }
  }, [iniziativa, formContext.values.applicantIsAccountholder, isUnavailable, isUntraceableCensus]);

  const onChangeApplicantIsAccountholder = useEventCallback(() => {
    formContext.setFieldValue(
      'applicantIsAccountholder',
      !formContext.values.applicantIsAccountholder
    );
  });

  const onChangeType = useEventCallback((type: TypeInitiative, typePracticeEdit?: string) => {
    formContext.setValues({
      ...(formContext.values || {}),
      typeInitiative: type,
      typePractice: typePracticeEdit || typePractice,
    });
  });

  const cleanButton = useEventCallback(() => {
    formContext.setValues({
      applicantIsAccountholder: false,
      stepBozza: formContext.values.stepBozza || 0,
      typeInitiative: formContext.values.typeInitiative || undefined,
      typePractice,
      accountholderData: {
        generality: {
          fiscalCode: '',
          gender: '',
          name: '',
          surname: '',
          withoutName: false,
          withoutSurname: false,
          residenceOrigin: {
            origin: '',
            previousMunicipality: '',
          },
        },
        identificationDocument: {
          documentNumber: undefined,
          isForeign: false,
          issuedDate: undefined,
          municipality: null,
          placeForeign: undefined,
        },
      },
      applicantData: {
        generality: {
          fiscalCode: '',
          gender: '',
          name: '',
          surname: '',
          withoutName: false,
          withoutSurname: false,
          identificationDocument: {
            documentNumber: '',
            issuedDate: null,
            issuedPlace: '',
          },
        },
        identificationDocument: {
          documentNumber: undefined,
          isForeign: false,
          issuedDate: undefined,
          municipality: null,
          placeForeign: undefined,
        },
      },
    } as RegisterOfficeVM);
  });

  const onSaveInitiative = useEventCallback(() => {
    if (!Boolean(formContext.values.typeInitiative)) {
      // se non presente non fare chiamata
      return;
    }

    const data = {
      key: {
        codiceEnte: codeEnte,
        tipoRegistro: typeRegister,
        idAnnoPratica: yearId ? Number.parseInt(yearId, 10) : undefined,
        progressivoPratica: progressive ? Number.parseInt(progressive, 10) : undefined,
      },
      tipoIniziativa: formContext.values.typeInitiative,
    };

    const dataTemporary = {
      key: {
        codiceEnte: codeEnte,
        tipoRegistro: typeRegister,
        idAnnoPratica: yearId ? Number.parseInt(yearId, 10) : undefined,
        progressivoPratica: progressive ? Number.parseInt(progressive, 10) : undefined,
      },
      tipoIniziativa: formContext.values.typeInitiative,
      tipoPratica: formContext.values.typePractice || 'PTR',
    };

    (
      updateData({
        data: ForPracticeTemporary ? dataTemporary : data,
        url: ForPracticeTemporary
          ? `${__PRACTICE_API_URL__}/pratiche/aggiornamento-pratica/tipo-pratica`
          : `${__PRACTICE_API_URL__}/pratiche/aggiornamento-pratica/tipo-iniziativa`,
      }) as ResponsePromise<void>
    ).then(() => {
      toast.success('Operazione eseguita con successo');

      setTipoIniziativa(formContext.values.typeInitiative);

      setOpenModalInitiative(false);
    });
  });

  const setModalNewPerson = useEventCallback((open: boolean, idSipo?: string, idAnpr?: string) => {
    setOpenModalNewPerson({ open, id: idSipo || '', idAnpr: idAnpr || '' });
  });

  const onChangeResidence = useEventCallback(
    (key: keyof ResidenceOrigin, value: ResidenceOrigin[keyof ResidenceOrigin]) => {
      formContext.setFieldValue('accountholderData', {
        ...(formContext.values.accountholderData || {}),
        residenceOrigin: {
          ...(formContext.values.accountholderData?.residenceOrigin || {}),
          [key]: value,
        },
      });
    }
  );

  const onChangeValuesResidence = useEventCallback((values: ResidenceOrigin) => {
    formContext.setFieldValue('accountholderData', {
      ...(formContext.values.accountholderData || {}),
      residenceOrigin: {
        ...(formContext.values.accountholderData?.residenceOrigin || {}),
        ...values,
      },
    });
  });

  const disabledPrintDeclaration: boolean = useMemo(() => {
    if (!Boolean(formContext.values.accountholderData?.generality?.fiscalCode)) {
      return true;
    }

    if (
      !formContext.values.applicantIsAccountholder &&
      !Boolean(formContext.values.applicantData?.generality?.fiscalCode)
    ) {
      return true;
    }

    if (ForPracticeTemporary && !formContext.values.residenceTemporary?.isSaved) {
      return true;
    }

    return false;
  }, [
    formContext.values.accountholderData,
    formContext.values.applicantData,
    formContext.values.applicantIsAccountholder,
    formContext.values.residenceTemporary,
  ]);

  return (
    <>
      {formContext.values?.typeInitiative === TypeInitiative.Anpr &&
      formContext.values?.numberPracticeAnpr ? (
        <Typography padding="17px 0 17px 0" fontWeight="bold" fontSize="18px">
          {`Pratica ANPR nr: ${formContext.values.numberPracticeAnpr}`}
        </Typography>
      ) : null}
      <FormControlLabel
        control={
          <Checkbox
            id="applicanteIsAccountholder"
            checked={formContext.values?.applicantIsAccountholder || false}
            onChange={() => onChangeApplicantIsAccountholder()}
            inputProps={{ 'aria-label': "il richiedente coincide con l'intestatario" }}
            disabled={
              disabled ||
              disabledAnpr ||
              disabledElix ||
              (formContext.values.typeInitiative === TypeInitiative.idUfficio &&
                typeRegister !== TypeRegister.Residence &&
                typeRegister !== TypeRegister.Emigration) ||
              isUnavailable ||
              isUntraceableCensus
            }
          />
        }
        label="il richiedente coincide con l'intestatario"
      />
      <AccordionApplicantGenerality
        isOpen={formContext.values?.applicantIsAccountholder || false}
        formContext={formContext}
        disabled={disabled || disabledAnpr || disabledElix}
        applicantIsAccountholder={formContext.values?.applicantIsAccountholder}
        setOpenModalNewPerson={setModalNewPerson}
        typeRegister={typeRegister}
        yearId={yearId}
        progressive={progressive}
      />
      <IdentificationDocumentComponent
        isOpen={formContext.values?.applicantIsAccountholder || false}
        disabled={disabled || disabledAnpr || disabledElix}
        applicantIsAccountholder={formContext.values?.applicantIsAccountholder}
        formContext={formContext}
      />
      <AccordionAccountholderGenerality
        formContext={formContext}
        isOpen={!formContext.values?.applicantIsAccountholder}
        setOpenModalNewPerson={setModalNewPerson}
        disabled={disabled || disabledAnpr || disabledElix}
        typeRegister={typeRegister}
        yearId={yearId}
        progressive={progressive}
        setIntestatarioIsAnpr={setIntestatarioIsAnpr}
      />
      {formContext.values?.applicantIsAccountholder ? (
        <IdentificationDocumentAccountholder
          disabled={disabledAnpr || disabledElix}
          formContext={formContext}
        />
      ) : undefined}
      <Citizenship formContext={formContext} disabled={disabled || disabledAnpr || disabledElix} />
      {typeRegister === TypeRegister.Immigration || ForPracticeTemporary ? (
        <ResidenceOriginComponent
          onChange={onChangeResidence}
          onChangeValues={onChangeValuesResidence}
          disabled={disabledElix}
          disabledFields={{
            previousOrigin: disabledAnpr,
          }}
          typeRegister={typeRegister}
          fromDate={DateTime.now()}
          values={formContext.values.accountholderData?.residenceOrigin}
        />
      ) : undefined}
      {ForPracticeTemporary ? (
        <AccordionResidenceTemporary
          disabled={disabled || disabledAnpr || disabledElix}
          formContext={formContext}
          codeEnte={codeEnte}
          yearId={yearId}
          typeRegister={typeRegister}
          progressive={progressive}
        />
      ) : undefined}
      <div className="pmi-pt-3 pmi-d-flex pmi-justify-content-center">
        <div className="pmi-w-25 pmi-w-lg-15">
          <PmiButton
            variant="outlined"
            fullWidth
            color="cdmBrand"
            onClick={cleanButton}
            disabled={disabled || disabledAnpr || disabledElix}
          >
            Ripulisci
          </PmiButton>
        </div>

        {ForPracticeTemporary ? (
          <div className="pmi-pl-2 pmi-w-50">
            <PmiButton
              variant="outlined"
              fullWidth
              color="cdmBrandThird"
              onClick={printDeclarationDeclarant}
              disabled={disabledPrintDeclaration}
            >
              {ForPracticeTemporary ? 'STAMPA DICHIARAZIONE' : 'STAMPA DICHIARAZIONE DICHIARANTE'}
            </PmiButton>
          </div>
        ) : undefined}

        <div className="pmi-pl-2 pmi-w-25 pmi-w-lg-15">
          <PmiButton
            variant="outlined"
            fullWidth
            disabled={
              (isUnavailable || isUntraceableCensus) &&
              formContext.values.typeInitiative === TypeInitiative.idUfficio
                ? false
                : ForPracticeTemporary && isConfermaButtonClicked === false
            }
            color="cdmBrandThird"
            onClick={() => {
              // if (disabled) {
              //   onNext();

              //   return;
              // }
              formContext.handleSubmit();
            }}
          >
            Avanti
          </PmiButton>
        </div>
      </div>
      <ModalInitiative
        open={openModalInitiative}
        onChange={onChangeType}
        onSave={onSaveInitiative}
        typeSelected={formContext.values.typeInitiative}
        typePractice={formContext.values.typePractice || ''}
        ForPracticeTemporary={ForPracticeTemporary}
      />
      <NewRegistrationPersonModal
        open={openModalNewPerson.open}
        onClose={() => setOpenModalNewPerson({ open: false, id: '', idAnpr: '' })}
        onNo={() => setOpenModalNewPerson({ open: false, id: '', idAnpr: '' })}
        onYes={() => onYesNewPerson()}
        id={openModalNewPerson.id}
        idAnpr={openModalNewPerson.idAnpr}
      />
      <ModalViewerPDF
        open={pdfModal.open}
        data={pdfModal.data}
        name={pdfModal.name}
        title="Riepilogo Stampa"
        onClose={() => {
          setPdfModal({ data: '', open: false, name: '' });
          if (!ForPracticeTemporary) {
            setDeclarationSuccessModal(true);
          } else {
            setConfermaButton(true);
          }
        }}
      />
      <PrintSuccessModalDeclarationDeclarant
        isOpen={isDeclarationSuccessModal}
        onConfirm={() => {
          setConfermaButton(true);
          setDeclarationSuccessModal(false);
        }}
        rePrint={() => {
          printDeclarationDeclarant().then(() => {
            setDeclarationSuccessModal(false);
          });
        }}
        onClose={() => setDeclarationSuccessModal(false)}
      />
    </>
  );
};

AccordionStepRegisterOfficeComponent.displayName = 'Accordion register office component';

const AccordionStepRegisterOffice = memo(AccordionStepRegisterOfficeComponent);

export default AccordionStepRegisterOffice;
